import React, { useMemo } from 'react';
import { getChartsShowCount, sortMapAuxiliaryFunc } from '@op/utils';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { useIntl } from 'umi';

type EChartsOptions = echarts.EChartsOption;
export default function EChart({
  tooltipFormatterFunc,
  xAxisData,
  seriesDataArray,
  lineLegendFormatter,
  dateType,
  height = 300,
  colorObj,
  isStack,
  title,
  loading,
  chartType = 'line',
  sortObj = {},
  connectNulls = true,
}: {
  tooltipFormatterFunc: (value: any, title?: string) => string | undefined;
  xAxisData: Array<any>;
  seriesDataArray: Array<any>;
  lineLegendFormatter?: (value: any) => string;
  loading: boolean;
  dateType: any;
  height: number;
  colorObj?: { [key: string]: string };
  isStack?: boolean;
  title?: string;
  connectNulls?: boolean;
  chartType: 'bar' | 'line';
  sortObj?: { [key: string]: number };
}) {
  const { formatMessage } = useIntl();
  // 设置图形配置
  const makeupOptions = () => {
    const seriesData: EChartsOptions['series'] = [];
    const legendData: Array<any> = [];
    seriesDataArray.forEach(({ data, seriesOptions = {}, name }) => {
      const color = colorObj?.[name];
      let realData = data;
      // if (!connectNulls) {
      //   realData = realData.map((item: any) => (item === 0 ? null : item));
      // }
      // console.log(realData, name);
      const seriesItem: any = {
        data: realData,
        type: chartType as any,
        name: name,
        color: color,
        ...seriesOptions,
      };
      if (isStack) {
        seriesItem['stack'] = 'Total';
        seriesItem['areaStyle'] = { opacity: 0.25 };
      }
      seriesData.push(seriesItem);
      legendData.push({
        name: name,
        icon: 'circle',
        color,
        itemStyle: { color },
      });
    });
    legendData.sort(sortMapAuxiliaryFunc(sortObj, (item) => item.name));

    const opts: EChartsOptions = {
      tooltip: {
        formatter: (param) => tooltipFormatterFunc(param, title),
        position: (pos, params, dom, rect, size) => {
          // 鼠标在左侧时 tooltip 显示到右侧，鼠标在右侧时 tooltip 显示到左侧。
          const [canvasWidth, canvasHeight] = size.viewSize;
          const [mouseX, mouseY] = pos;
          if (mouseX < canvasWidth / 2) {
            return [mouseX + 10, mouseY / 6];
          }
          return [mouseX - 240, mouseY / 6];
        },
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, dateType);
      }),
      legend: {
        data: legendData,
        formatter: (name) => {
          if (lineLegendFormatter instanceof Function) {
            return lineLegendFormatter(name);
          }
          return name;
        },
      },
      xAxis: {
        data: xAxisData,
        axisLabel: {
          align: 'center',
          formatter: (value) => formatTimeXAxisText(value, dateType),
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value) => {
            const data = getChartsShowCount(Math.abs(value), 1);
            return value >= 0 ? data : `-${data}`;
          },
        },
      },
      series: seriesData,
    };

    return opts;
  };

  const options = useMemo(() => {
    return makeupOptions();
  }, [seriesDataArray, xAxisData, formatMessage, chartType]);

  return (
    <EChartCommon
      options={options}
      loading={loading}
      height={height}
      optionsProps={{ type: chartType }}
      empty={seriesDataArray?.length === 0}
    />
  );
}
